import React from "react";
import { Link } from "react-router-dom";
import { useFromState } from "@/lib/navContext";

/**
 * Componente unico per i link a scheda cliente.
 * USO OBBLIGATORIO ovunque nel CRM dove appare un nome cliente cliccabile.
 *
 * Esempi:
 *   <ClientLink clientId={c.id}>{c.cognome} {c.nome}</ClientLink>
 *   <ClientLink clientId={t.client_id} name={t.client?.cognome + " " + t.client?.nome} />
 *   <ClientLink clientId={c.id} variant="strong">Mario Rossi</ClientLink>
 *
 * Behavior:
 *   - Se clientId mancante: render passivo (no link) per evitare /clienti/undefined
 *   - Default style: navy + underline on hover, transition smooth
 *   - variant="strong" → grassetto medio (default in tabelle)
 *   - variant="muted" → grigio (default in elementi secondari)
 *   - className → aggiunge classi extra
 *   - mobile=true → naviga a /m/clienti/:id (per mobile shell)
 */
export default function ClientLink({
  clientId,
  name,
  children,
  variant = "strong",
  className = "",
  mobile = false,
  onClick,
  testIdPrefix = "client-link",
  title,
}) {
  const label = children || name || "Cliente";
  const fromState = useFromState();

  if (!clientId) {
    return <span className={`text-slate-700 ${className}`}>{label}</span>;
  }

  const base = "transition-colors hover:text-[#B7955B] hover:underline underline-offset-2 decoration-[#B7955B]/60";
  const variants = {
    strong: "text-slate-900 font-medium",
    medium: "text-slate-800",
    muted: "text-slate-600",
    light: "text-slate-500",
    serif: "font-serif text-slate-900",
  };
  const variantCls = variants[variant] || variants.strong;

  const to = mobile ? `/m/clienti/${clientId}` : `/clienti/${clientId}`;

  return (
    <Link
      to={to}
      state={fromState}
      className={`${base} ${variantCls} ${className}`}
      data-testid={`${testIdPrefix}-${clientId}`}
      onClick={onClick}
      title={title || `Apri scheda di ${typeof label === "string" ? label : "cliente"}`}
    >
      {label}
    </Link>
  );
}
